import type {
  GovernorLiveAction,
} from '@/lib/governorEventBus';

export const FOREGROUND_CALL_REQUIREMENTS = [
  'session-created',
  'incoming-delivery',
  'receiver-accepted',
  'connecting',
  'connected',
  'ended',
] as const;

export type ForegroundCallRequirement =
  typeof FOREGROUND_CALL_REQUIREMENTS[number];

export type ForegroundRequirementResult =
  Readonly<{
    requirement:
      ForegroundCallRequirement;
    passed: boolean;
    evidenceId: string | null;
    stage: string | null;
    source: string | null;
    observedAt: string | null;
  }>;

export type ForegroundCertificationProjection =
  Readonly<{
    callSessionId: string;
    recordsExamined: number;
    requirements:
      readonly ForegroundRequirementResult[];
    passedCount: number;
    requiredCount: number;
    lifecycleComplete: boolean;
    blockingFailures: number;
    status:
      | 'waiting'
      | 'failed'
      | 'lifecycle-complete';
    certificationAllowed: false;
    certificationReason: string;
  }>;

type UnknownRecord =
  Record<string, unknown>;

const asRecord = (
  value: unknown,
): UnknownRecord =>
  value !== null &&
  typeof value === 'object'
    ? value as UnknownRecord
    : {};

const asString = (
  value: unknown,
): string =>
  typeof value === 'string'
    ? value.trim()
    : '';

const searchableEventText = (
  action: GovernorLiveAction,
): string => {
  const detail =
    asRecord(action.detail);

  return [
    detail.stage,
    detail.status,
    detail.source,
    detail.step,
    detail.lifecycleState,
    detail.state,
    detail.action,
    detail.reason,
    detail.error,
  ]
    .map(asString)
    .join(' ')
    .toLowerCase();
};

const eventSessionId = (
  action: GovernorLiveAction,
): string => {
  const detail =
    asRecord(action.detail);

  return (
    asString(
      detail.callSessionId,
    ) ||
    asString(
      detail.call_session_id,
    ) ||
    asString(
      detail.callId,
    )
  );
};

const requirementTerms:
  Record<
    ForegroundCallRequirement,
    readonly string[]
  > = {
    'session-created': [
      'call_session_created',
      'session_created',
      'call session created',
    ],
    'incoming-delivery': [
      'incoming_call_delivery',
      'call_relay_delivery_accepted',
      'direct_incoming_call_delivery_attempted',
      'call detected',
      'incoming-call',
    ],
    'receiver-accepted': [
      'accepted',
      'acceptincomingcall',
      'native_call_action_consumed accept',
      'action accept',
    ],
    connecting: [
      'connecting',
      'connection started',
      'peer connection started',
    ],
    connected: [
      'connected',
      'remote media',
      'peer connection connected',
      'accepted_to_live_modal',
    ],
    ended: [
      'ended',
      'call ended',
      'native_call_ended',
      'returned_to_messages',
    ],
  };

const matchesRequirement = (
  text: string,
  requirement:
    ForegroundCallRequirement,
): boolean =>
  requirementTerms[
    requirement
  ].some(
    term =>
      text.includes(term),
  );

export function projectForegroundCallCertification(
  actions:
    readonly GovernorLiveAction[],
  callSessionId: string,
): ForegroundCertificationProjection {
  const normalizedSessionId =
    callSessionId.trim();

  const records =
    actions.filter(
      action =>
        action.type ===
          'governor_calls_runtime_event' &&
        normalizedSessionId.length > 0 &&
        eventSessionId(action) ===
          normalizedSessionId,
    );

  const requirements =
    FOREGROUND_CALL_REQUIREMENTS.map(
      requirement => {
        const action =
          records.find(
            candidate =>
              matchesRequirement(
                searchableEventText(
                  candidate,
                ),
                requirement,
              ),
          ) ?? null;

        const detail =
          asRecord(
            action?.detail,
          );

        return Object.freeze({
          requirement,
          passed:
            action !== null,
          evidenceId:
            action?.id ?? null,
          stage:
            action
              ? asString(
                  detail.stage,
                ) || null
              : null,
          source:
            action
              ? asString(
                  detail.source,
                ) || null
              : null,
          observedAt:
            action
              ? (
                  asString(
                    detail.timestamp,
                  ) ||
                  action.createdAt
                )
              : null,
        });
      },
    );

  const passedCount =
    requirements.filter(
      requirement =>
        requirement.passed,
    ).length;

  const blockingFailures =
    records.filter(action => {
      const detail =
        asRecord(
          action.detail,
        );

      const status =
        asString(
          detail.status,
        ).toLowerCase();

      return (
        status === 'failure' ||
        status === 'timeout' ||
        status === 'waiting'
      );
    }).length;

  const lifecycleComplete =
    passedCount ===
      requirements.length &&
    blockingFailures === 0;

  return Object.freeze({
    callSessionId:
      normalizedSessionId,
    recordsExamined:
      records.length,
    requirements:
      Object.freeze(
        requirements,
      ),
    passedCount,
    requiredCount:
      requirements.length,
    lifecycleComplete,
    blockingFailures,
    status:
      blockingFailures > 0
        ? 'failed'
        : lifecycleComplete
          ? 'lifecycle-complete'
          : 'waiting',
    certificationAllowed: false,
    certificationReason:
      normalizedSessionId.length === 0
        ? 'Enter a real call-session ID before evaluating evidence.'
        : blockingFailures > 0
          ? 'The correlated call contains failure, timeout, or waiting evidence.'
          : lifecycleComplete
            ? 'The foreground lifecycle is complete, but audio, video, and two-device observations still require operator confirmation.'
            : 'One or more required foreground lifecycle stages are missing.',
  });
}
